import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PenSquare, Trash2, Eye, EyeOff, Plus, Search, Filter, Clock, BarChart2, AlertCircle, Loader2, Sparkles } from 'lucide-react';
import Header from '../../components/navigation/Header';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_COLORS = {
  published: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  draft: 'bg-amber-50 text-amber-700 border border-amber-200',
};

const BlogAdminPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase?.from('blog_posts')?.select('id, title, slug, status, category, tags, read_time, published_at, created_at, updated_at, excerpt')?.order('updated_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query?.eq('status', statusFilter);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      setPosts(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleDelete = async (postId) => {
    setActionLoading(postId);
    try {
      const { error: deleteError } = await supabase?.from('blog_posts')?.delete()?.eq('id', postId);
      if (deleteError) throw deleteError;
      setPosts(prev => prev?.filter(p => p?.id !== postId));
      setDeleteConfirm(null);
    } catch (err) {
      setError(err?.message || 'Failed to delete post');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleStatus = async (post) => {
    const newStatus = post?.status === 'published' ? 'draft' : 'published';
    setActionLoading(post?.id);
    try {
      const updateData = {
        status: newStatus,
        ...(newStatus === 'published' && !post?.published_at ? { published_at: new Date()?.toISOString() } : {}),
      };
      const { error: updateError } = await supabase?.from('blog_posts')?.update(updateData)?.eq('id', post?.id);
      if (updateError) throw updateError;
      setPosts(prev => prev?.map(p => p?.id === post?.id ? { ...p, ...updateData } : p));
    } catch (err) {
      setError(err?.message || 'Failed to update post status');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredPosts = posts?.filter(p =>
    p?.title?.toLowerCase()?.includes(search?.toLowerCase()) ||
    p?.category?.toLowerCase()?.includes(search?.toLowerCase()) ||
    p?.tags?.some(t => t?.toLowerCase()?.includes(search?.toLowerCase()))
  );

  const publishedCount = posts?.filter(p => p?.status === 'published')?.length;
  const draftCount = posts?.filter(p => p?.status === 'draft')?.length;

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Header user={user} onNavigate={() => {}} onPurchaseCredits={() => {}} onLogout={() => {}} />
      {/* Hero */}
      <div className="relative w-full overflow-hidden" style={{ paddingTop: '64px' }}>
        <div className="relative h-40 bg-gradient-to-br from-gray-900 via-violet-950 to-gray-900 flex items-end">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-900/40 to-cyan-900/20" />
          <div className="absolute top-4 right-1/4 w-40 h-40 bg-violet-600/20 rounded-full blur-3xl" />
          <div className="relative z-10 max-w-6xl mx-auto px-6 pb-6 w-full flex items-end justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-600 to-cyan-500 flex items-center justify-center">
                  <Sparkles size={12} className="text-white" />
                </div>
                <span className="text-white/70 text-xs font-medium">Blog Admin</span>
              </div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Crimson Text, serif' }}>
                Manage Blog Posts
              </h1>
            </div>
            <Link
              to="/blog-admin/editor"
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-sm font-semibold hover:opacity-90 transition-all shadow-lg shadow-violet-900/30"
            >
              <Plus size={15} /> New Post
            </Link>
          </div>
        </div>
      </div>
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Total Posts', value: posts?.length, icon: BarChart2, color: 'text-violet-600' },
            { label: 'Published', value: publishedCount, icon: Eye, color: 'text-emerald-600' },
            { label: 'Drafts', value: draftCount, icon: EyeOff, color: 'text-amber-600' },
          ]?.map(stat => (
            <div key={stat?.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center ${stat?.color}`}>
                <stat.icon size={18} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stat?.value}</div>
                <div className="text-xs text-gray-500">{stat?.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search posts by title, category, or tag..."
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 transition-all"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-gray-400" />
            {['all', 'published', 'draft']?.map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-2 text-xs font-semibold rounded-lg transition-all capitalize ${
                  statusFilter === s
                    ? 'bg-violet-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-violet-300'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 p-4 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            <AlertCircle size={15} />
            {error}
          </div>
        )}

        {/* Posts List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-violet-500" />
          </div>
        ) : filteredPosts?.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto mb-4">
              <PenSquare size={24} className="text-violet-400" />
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {search ? 'No posts match your search.' : 'No posts yet. Create your first post!'}
            </p>
            {!search && (
              <Link
                to="/blog-admin/editor"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition-colors"
              >
                <Plus size={14} /> Create First Post
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPosts?.map((post, idx) => (
              <motion.div
                key={post?.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.04, duration: 0.3 }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-5"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${STATUS_COLORS?.[post?.status] || STATUS_COLORS?.draft}`}>
                        {post?.status}
                      </span>
                      <span className="text-xs text-violet-600 bg-violet-50 px-2.5 py-0.5 rounded-full font-medium">
                        {post?.category}
                      </span>
                      {post?.tags?.slice(0, 3)?.map(tag => (
                        <span key={tag} className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-base font-bold text-gray-900 leading-snug mb-1 truncate">
                      {post?.title || 'Untitled Post'}
                    </h3>
                    <p className="text-sm text-gray-500 line-clamp-1 mb-2">{post?.excerpt}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><Clock size={11} /> {post?.read_time}</span>
                      <span>Updated {new Date(post?.updated_at)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      {post?.published_at && (
                        <span>Published {new Date(post?.published_at)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleToggleStatus(post)}
                      disabled={actionLoading === post?.id}
                      title={post?.status === 'published' ? 'Unpublish' : 'Publish'}
                      className={`p-2 rounded-lg transition-all ${
                        post?.status === 'published' ?'text-emerald-600 bg-emerald-50 hover:bg-emerald-100' :'text-amber-600 bg-amber-50 hover:bg-amber-100'
                      }`}
                    >
                      {actionLoading === post?.id ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : post?.status === 'published' ? (
                        <Eye size={15} />
                      ) : (
                        <EyeOff size={15} />
                      )}
                    </button>
                    <Link
                      to={`/blog-admin/editor/${post?.id}`}
                      className="p-2 rounded-lg text-violet-600 bg-violet-50 hover:bg-violet-100 transition-all"
                      title="Edit post"
                    >
                      <PenSquare size={15} />
                    </Link>
                    {deleteConfirm === post?.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDelete(post?.id)}
                          disabled={actionLoading === post?.id}
                          className="px-2.5 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="px-2.5 py-1.5 text-xs font-semibold bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(post?.id)}
                        className="p-2 rounded-lg text-red-500 bg-red-50 hover:bg-red-100 transition-all"
                        title="Delete post"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default BlogAdminPage;
